import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon, TicketIcon } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatusPill, statusLabel, type StatusTone } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label, Skeleton } from "@/components/ui/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDateValue } from "@/entities/format";
import { type TicketState, usePortalSolutions, usePortalTickets, useRaiseTicket } from "./api";

const TONE: Record<string, StatusTone> = {
  approval_requested: "warning",
  approved: "info",
  in_progress: "info",
  awaiting_sign_off: "warning",
  pending_info: "warning",
  hold: "neutral",
  completed: "success",
  cancelled: "neutral",
  rejected: "danger",
};

const PRIORITIES = [
  { value: "p1", label: "P1 – Critical" },
  { value: "p2", label: "P2 – High" },
  { value: "p3", label: "P3 – Medium" },
  { value: "p4", label: "P4 – Low" },
];

const day = (v: string | null | undefined) => formatDateValue(v) || "—";

/** The client portal: the client's tickets and a way to raise a new one. URL: `state`. */
export function PortalTicketsPage() {
  const search = useSearch({ strict: false }) as { state?: string };
  const navigate = useNavigate();
  const state: TicketState = search.state === "closed" ? "closed" : search.state === "all" ? "all" : "open";
  const tickets = usePortalTickets(state);
  const [raising, setRaising] = useState(false);
  const setState = (next: string | undefined) => void navigate({ to: "/portal/tickets", search: next ? { state: next } : {} });

  return (
    <>
      <PageHeader
        title="Tickets"
        description="Raise a support ticket and follow its progress."
        actions={
          <Button onClick={() => setRaising(true)}>
            <PlusIcon /> Raise a ticket
          </Button>
        }
      />
      <Tabs value={state} onValueChange={(v) => setState(v === "open" ? undefined : v)} className="mb-3">
        <TabsList aria-label="Which tickets">
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>
      {tickets.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : tickets.isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load your tickets. Try again shortly.</p>
      ) : !tickets.data.length ? (
        <EmptyState
          icon={TicketIcon}
          title={state === "open" ? "No open tickets" : "No tickets"}
          description="Tickets you and your colleagues raise appear here."
          action={
            <Button variant="outline" onClick={() => setRaising(true)}>
              <PlusIcon /> Raise a ticket
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm" aria-label="Tickets">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b">
                <th className="px-4 py-2 font-medium">Ticket</th>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="hidden px-4 py-2 font-medium md:table-cell">Solution</th>
                <th className="hidden px-4 py-2 font-medium lg:table-cell">Requested by</th>
                <th className="px-4 py-2 font-medium">Priority</th>
                <th className="px-4 py-2 font-medium">Raised</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tickets.data.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap text-muted-foreground">{t.number}</td>
                  <td className="px-4 py-2.5 font-medium break-words">{t.title}</td>
                  <td className="hidden px-4 py-2.5 md:table-cell">{t.bau_solution ?? "—"}</td>
                  <td className="hidden px-4 py-2.5 lg:table-cell">{t.requested_by ?? "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{t.priority.toUpperCase()}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{day(t.requested_at)}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill tone={TONE[t.status] ?? "neutral"}>{statusLabel(t.status)}</StatusPill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {raising && <RaiseTicketDialog onClose={() => setRaising(false)} onRaised={() => setState(undefined)} />}
    </>
  );
}

function RaiseTicketDialog({ onClose, onRaised }: { onClose: () => void; onRaised: () => void }) {
  const solutions = usePortalSolutions();
  const raise = useRaiseTicket();
  const [form, setForm] = useState({ title: "", detailed_requirement: "", bau_solution_id: "", priority: "p3" });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const ready = form.title.trim().length >= 3 && form.detailed_requirement.trim().length >= 3 && form.bau_solution_id;
  const submit = () =>
    raise.mutate(form, {
      onSuccess: (t) => {
        toast.success(`Ticket ${t.number} raised`);
        onClose();
        onRaised();
      },
      onError: (e) => toast.error("Couldn't raise the ticket", { description: e instanceof Error ? e.message : undefined }),
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Raise a ticket</DialogTitle>
          <DialogDescription>Tell us what you need.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-solution">Solution</Label>
            <Select value={form.bau_solution_id || undefined} onValueChange={(v) => set("bau_solution_id", v)}>
              <SelectTrigger id="pt-solution" aria-label="Solution">
                <SelectValue placeholder={solutions.isPending ? "Loading…" : "Choose the solution this is about"} />
              </SelectTrigger>
              <SelectContent>
                {(solutions.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.department ? ` (${s.department})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-title">Title</Label>
            <Input id="pt-title" maxLength={100} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-priority">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger id="pt-priority" aria-label="Priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-detail">What do you need?</Label>
            <Textarea id="pt-detail" rows={5} value={form.detailed_requirement} onChange={(e) => set("detailed_requirement", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready || raise.isPending} onClick={submit}>
            {raise.isPending ? "Raising…" : "Raise ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
